import { useMemo } from 'react';
import { useCurrentUser } from './useApi';

interface UseUserPermissionsReturn {
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasAllPermissions: (...permissions: string[]) => boolean;
  permissions: string[];
  isLoading: boolean;
}

/**
 * Hook to check user permissions
 * @returns Object with permission checking utilities
 */
export const useUserPermissions = (): UseUserPermissionsReturn => {
  const { data: user, isLoading } = useCurrentUser();
  
  const permissions = useMemo(() => {
    return user?.permissions || [];
  }, [user]);
  
  const hasPermission = useMemo(() => {
    return (permission: string): boolean => {
      return permissions.includes(permission);
    };
  }, [permissions]);
  
  const hasAnyPermission = useMemo(() => {
    return (...requiredPermissions: string[]): boolean => {
      if (requiredPermissions.length === 0) return true;
      return requiredPermissions.some(permission => permissions.includes(permission));
    };
  }, [permissions]);
  
  const hasAllPermissions = useMemo(() => {
    return (...requiredPermissions: string[]): boolean => {
      if (requiredPermissions.length === 0) return true;
      return requiredPermissions.every(permission => permissions.includes(permission));
    };
  }, [permissions]);
  
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    permissions,
    isLoading,
  };
};